"use client";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const QUESTIONS: ("q1" | "q2" | "q3" | "q4" | "q5" | "q6" | "q7" | "q8")[] = [
  "q1",
  "q2",
  "q3",
  "q4",
  "q5",
  "q6",
  "q7",
  "q8",
];

export const FaqSection = () => {
  const t = useTranslations("faq");
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="relative w-full py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-4 md:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-red-300">
            <QuestionIcon className="h-3 w-3 text-red-400" />
            <span>{t("eyebrow")}</span>
          </div>
          <h2 className="mt-6 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white">
            {t("heading")}
          </h2>
        </div>

        <div className="mt-14">
          {QUESTIONS.map((q, idx) => {
            const isOpen = open === idx;
            const aKey = `a${q.slice(1)}` as
              | "a1"
              | "a2"
              | "a3"
              | "a4"
              | "a5"
              | "a6"
              | "a7"
              | "a8";
            return (
              <div
                key={q}
                className={cn(
                  "border-b border-neutral-800",
                  idx === 0 && "border-t border-neutral-800"
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="w-full flex items-start justify-between gap-6 py-5 md:py-6 text-left transition-colors hover:text-red-400"
                  aria-expanded={isOpen}
                >
                  <span className="text-base md:text-lg font-semibold text-neutral-100">
                    {t(q)}
                  </span>
                  <span
                    className={cn(
                      "mt-1 shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/10 ring-1 ring-red-500/30 text-red-400 transition-transform",
                      isOpen && "rotate-45"
                    )}
                  >
                    <PlusIcon className="h-3 w-3" />
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-6 md:pb-7 pr-12 text-sm md:text-base text-neutral-400 leading-relaxed max-w-prose">
                    {t(aKey)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const QuestionIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);
